import { SyntheticEvent, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Alert, Button, Group, Modal, PinInput, Stack, Text, TextInput } from "@mantine/core";
import { useStores } from "@/hooks/useStores";
import type { SignInError } from "@/stores/AuthStore";

const CODE_LENGTH = 8;

const messages: Record<SignInError, string> = {
    invalid_email: "Det där ser inte ut som en giltig e-postadress.",
    rate_limited: "Du har begärt för många koder. Vänta en stund och försök igen.",
    invalid_or_expired_code: "Koden stämmer inte eller har gått ut. Kontrollera den eller begär en ny.",
    send_failed: "Vi kunde inte skicka e-posten just nu. Försök igen om en stund.",
    network: "Något gick fel. Kontrollera anslutningen och försök igen.",
};

export const SignInModal = observer(() => {
    const { appStore, authStore } = useStores();
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");

    // Start over each time the dialog is opened
    useEffect(() => {
        if (appStore.showSignIn) {
            setCode("");
            authStore.changeEmail();
        }
    }, [appStore.showSignIn, authStore]);

    const close = () => appStore.setShowSignIn(false);
    const waiting = authStore.error === "rate_limited" && authStore.retryAfter > 0;

    const sendCode = async (event?: SyntheticEvent) => {
        event?.preventDefault();
        await authStore.requestCode(email);
    };

    const submitCode = async (value: string) => {
        if (value.length !== CODE_LENGTH) return;
        if (await authStore.verifyCode(value)) {
            close();
        } else {
            setCode("");
        }
    };

    return (
        <Modal centered onClose={close} opened={appStore.showSignIn} title="Logga in">
            {authStore.pendingEmail === null ? (
                <form onSubmit={sendCode}>
                    <Stack gap="md">
                        <Text size="sm">
                            Skriv in din e-postadress så skickar vi en engångskod. Inget lösenord behövs.
                        </Text>
                        <TextInput
                            autoComplete="email"
                            data-autofocus
                            inputMode="email"
                            label="E-post"
                            onChange={(event) => setEmail(event.currentTarget.value)}
                            placeholder="du@exempel.se"
                            type="email"
                            value={email}
                        />
                        {authStore.error && (
                            <Alert color="red" role="alert">
                                {messages[authStore.error]}
                                {waiting && ` (cirka ${authStore.retryAfter} sekunder)`}
                            </Alert>
                        )}
                        <Group justify="space-between">
                            {waiting ? (
                                <Button onClick={() => authStore.enterCode(email)} variant="subtle">
                                    Jag har redan en kod
                                </Button>
                            ) : (
                                <span />
                            )}
                            <Button disabled={email.trim().length === 0} loading={authStore.busy} type="submit">
                                Skicka kod
                            </Button>
                        </Group>
                    </Stack>
                </form>
            ) : (
                <Stack gap="md">
                    <Text size="sm">
                        Vi har skickat en kod med {CODE_LENGTH} siffror till <strong>{authStore.pendingEmail}</strong>.
                        Den gäller i 15 minuter.
                    </Text>
                    <PinInput
                        aria-label="Inloggningskod"
                        autoFocus
                        disabled={authStore.busy}
                        length={CODE_LENGTH}
                        oneTimeCode
                        onChange={setCode}
                        onComplete={submitCode}
                        type="number"
                        value={code}
                    />
                    {authStore.error && (
                        <Alert color="red" role="alert">
                            {messages[authStore.error]}
                        </Alert>
                    )}
                    <Group justify="space-between">
                        <Button onClick={() => authStore.changeEmail()} variant="subtle">
                            Ändra e-post
                        </Button>
                        <Button
                            disabled={authStore.busy}
                            onClick={async () => {
                                setCode("");
                                await authStore.requestCode(authStore.pendingEmail ?? email);
                            }}
                            variant="light">
                            Skicka ny kod
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
});
